import { createAsyncThunk, createSlice, PayloadAction, } from '@reduxjs/toolkit'
import { Auth } from '@shared/services/Auth';
import { redirectTo, setUser } from '@shared/store/auth.duck';
import { AppState } from '@app/store/state/app.state';
import { enrichError, Error, ErrorCode } from '@shared/utils/error-helper';
import { AuthType } from "@shared/utils/AuthUtils";

export interface MicrosoftLoginError {
  code: string;
  detail?: string;
}

export interface LoginPageState {
  smartError?: Error;
  // Kept in Redux (not StandardLogin state) because the Teams login flow sets the
  // global auth spinner, which unmounts the login page until the attempt ends.
  microsoftError?: MicrosoftLoginError;
  processing: boolean;
  email: string;
}

export const initialLoginPageState: LoginPageState = {
  processing: false,
  smartError: undefined,
  microsoftError: undefined,
  email: '',
}

export const loginWithInvite: any = createAsyncThunk(
  'loginPage/loginWithInvite',
  async (creds: any, thunkAPI) => {
    await thunkAPI.dispatch(loginActions.setProcessing(true));

    try {
      const user = await Auth.loginWithInvitation({
        email: creds.email.trim(),
        password: creds.password,
        inviteId: creds.inviteId,
        allowMarketingEmails: creds.allowMarketingEmails,
        authType: AuthType.Standard,
      });
      await thunkAPI.dispatch(setUser(user));
    } catch (err: any) {
      handleError(err, thunkAPI.dispatch);
    }
  }
)

export const login: any = createAsyncThunk(
  'loginPage/login',
  async (creds: any, thunkAPI) => {
    await thunkAPI.dispatch(loginActions.setProcessing(true));
    await thunkAPI.dispatch(loginActions.setEmail(creds.email));

    try {
      const user = await Auth.login({ email: creds.email, password: creds.password });
      await thunkAPI.dispatch(setUser(user));
    } catch (err: any) {
      console.log(err)
      handleError(JSON.parse(err?.message), thunkAPI.dispatch);
    }
  }
)

const loginPageSlice = createSlice({
  name: 'loginPage',
  initialState: initialLoginPageState,
  reducers: {
    setSmartError: (state, action) => ({ ...state, smartError: action.payload }),
    clearSmartError: (state) => ({ ...state, smartError: undefined }),
    setMicrosoftError: (state, action: PayloadAction<MicrosoftLoginError | undefined>) => ({ ...state, microsoftError: action.payload }),
    setProcessing: (state, action) => ({ ...state, processing: action.payload}),
    setEmail: (state, action) => ({ ...state, email: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state) => ({ ...state, processing: false, }))
      .addCase(loginWithInvite.fulfilled, (state) => ({ ...state, processing: false, }));
  }
})

export default loginPageSlice.reducer
export const loginActions = loginPageSlice.actions;

// Selectors
export const selectLoginErrorMessage = (state: AppState) => state.loginPage.smartError;
export const selectMicrosoftLoginError = (state: AppState) => state.loginPage.microsoftError;
const selectIsLoading = (state: AppState) => state.loginPage.processing;
export const selectEmail = (state: AppState) => state.loginPage.email;


function handleError(error: any, dispatch: any) {
  const enrichedError = enrichError(error.message);
  if (enrichedError?.id === ErrorCode.VerificationRequired) {
    dispatch(redirectTo('/verification-required'))
  } else {
    dispatch(loginActions.setSmartError(enrichedError));
    throw error;
  }
}
